import React, { useEffect, useState } from 'react';
import { AlertTriangle, Crown } from 'lucide-react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { FeatureType } from '../../utils/subscriptionUtils';
import { SubscriptionErrorEvent } from '../../utils/subscriptionInterceptor';

interface LimitErrorHandlerProps {
  error: SubscriptionErrorEvent;
  onClose: () => void;
  className?: string;
}

/**
 * Component that displays error messages when API requests fail due to subscription limits
 */
const LimitErrorHandler: React.FC<LimitErrorHandlerProps> = ({ error, onClose, className }) => {
  const { setShowUpgradeModal } = useSubscription();
  
  const handleDismiss = () => {
    onClose();
  };
  
  const getFeatureName = (feature: string): string => {
    const nameMap: Record<string, string> = {
      'contacts': 'Contacts',
      'groups': 'Groups',
      'ai': 'AI Queries',
      'todos': 'TODOs',
      'reminders': 'Reminders',
      'storage': 'Storage'
    };
    
    return nameMap[feature] || feature;
  };
  
  return (
    <div className={`bg-red-50 border border-red-300 rounded-lg shadow-lg p-4 z-50 animate-fade-in ${className}`}>
      <div className="flex justify-between items-start">
        <div className="flex items-start">
          <AlertTriangle className="text-red-600 mt-0.5 mr-2 flex-shrink-0" size={20} />
          <div>
            <h3 className="font-medium text-red-800">Subscription Limit Reached</h3>
            <p className="text-sm text-red-700 mt-1">
              You've reached your {getFeatureName(error.feature)} limit ({error.limit}).
            </p>
          </div>
        </div>
        <button 
          onClick={handleDismiss}
          className="text-red-700 hover:text-red-900"
          aria-label="Dismiss"
        >
          &times;
        </button>
      </div>
      <div className="mt-3">
        <button
          onClick={() => {
            setShowUpgradeModal(true);
            handleDismiss();
          }}
          className="w-full py-2 px-4 bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center justify-center gap-2"
        >
          <Crown size={16} />
          Upgrade Your Plan
        </button>
      </div>
    </div>
  );
};

export default LimitErrorHandler;